import { create } from 'zustand';
import type { Model } from '../types';
import { modelsService } from '../services/modelsService';
import { getToken } from '../lib/auth';

export interface ModelGroup {
  provider: string;
  models: Model[];
}

interface ModelsState {
  catalog: Model[]; // modelos de chat (rótulos, admin)
  embeddings: Model[]; // modelos de embedding (RAG)
  allowed: Model[]; // chat efetivos do usuário logado (Pessoa ∩ Área)
  loaded: boolean;
  loading: boolean;
  load: () => Promise<void>;
  reloadAllowed: () => Promise<void>;
  labelOf: (id: string) => string;
  isAllowed: (id: string) => boolean;
}

// Helper PURO (use com useMemo no componente — NÃO chame dentro de um seletor zustand,
// senão cria um array novo a cada render e dispara loop infinito).
export function groupModelsByProvider(models: Model[]): ModelGroup[] {
  const map = new Map<string, Model[]>();
  for (const m of models) {
    if (!map.has(m.provider)) map.set(m.provider, []);
    map.get(m.provider)!.push(m);
  }
  return Array.from(map.entries()).map(([provider, models]) => ({ provider, models }));
}

export const useModelsStore = create<ModelsState>((set, get) => {
  let sessionToken: string | null | undefined;
  let currentLoad: { token: string | null; promise: Promise<void> } | null = null;
  let allowedRevision = 0;
  let allowedVersion = 0;

  function syncSession(token: string | null) {
    if (sessionToken === token) return;
    sessionToken = token;
    currentLoad = null;
    allowedRevision += 1;
    allowedVersion += 1;
    // Não deixa modelos/permissões da sessão anterior visíveis durante a próxima carga.
    set({ catalog: [], embeddings: [], allowed: [], loaded: false, loading: false });
  }

  return {
    catalog: [],
    embeddings: [],
    allowed: [],
    loaded: false,
    loading: false,

    load: () => {
      const token = getToken();
      syncSession(token);
      if (currentLoad?.token === token) return currentLoad.promise;
      const version = allowedVersion;
      set({ loading: true });
      const pending = Promise.all([
        modelsService.list(),
        modelsService.listEmbeddings(),
        modelsService.listAllowed(),
      ]).then(([catalog, embeddings, allowed]) => {
        if (getToken() !== token || currentLoad?.promise !== pending) return;
        set({
          catalog,
          embeddings,
          loaded: true,
          // Uma atualização explícita de permissões prevalece sobre a carga inicial.
          ...(version === allowedVersion ? { allowed } : {}),
        });
      }).finally(() => {
        if (currentLoad?.promise === pending) {
          currentLoad = null;
          set({ loading: false });
        }
      });
      currentLoad = { token, promise: pending };
      return pending;
    },

    reloadAllowed: async () => {
      const token = getToken();
      syncSession(token);
      const revision = ++allowedRevision;
      allowedVersion += 1;
      const allowed = await modelsService.listAllowed();
      if (getToken() === token && sessionToken === token && revision === allowedRevision) {
        // Invalida também loads iniciados enquanto este refresh estava em andamento.
        allowedVersion += 1;
        set({ allowed });
      }
    },

    labelOf: (id) => {
      const m = get().catalog.find((x) => x.id === id) || get().embeddings.find((x) => x.id === id);
      return m ? m.label : id;
    },

    isAllowed: (id) => get().allowed.some((m) => m.id === id),
  };
});
